import { useRef, useEffect, useState } from 'react';
import MonacoEditor from 'react-monaco-editor';
import 'monaco-editor/min/vs/editor/editor.main.css';
import 'monaco-editor/esm/vs/base/browser/ui/codicons/codicon/codicon.css';

interface CodeEditorMonacoProps {
  code: string;
  cfg?: any;
  projectOptions?: any;
  mode: string;
  codeChanged: (code: string) => void;
  width?: string;
  height?: string;
  controlsRef?: any;
  onStatusChange?: (status: { lines: number; lineNumber: number; column: number }) => void;
  onModifiedChange?: (modified: boolean) => void;
  currentTheme?: string;
}

export const CodeEditorMonaco = (props: CodeEditorMonacoProps) => {
  const MonacoEditorAny: any = MonacoEditor;
  const editorRef = useRef(null);
  const monacoRef = useRef<any>(null);
  const [isSynced, setIsSynced] = useState(false);
  const baselineRef = useRef<string | undefined>(undefined);

  const getMode = (mode: string): string => {
    if (!mode) return 'plaintext';
    const m = mode.toLowerCase();
    if (m === 'c' || m === 'cpp' || m === 'c++') return 'cpp';
    if (m === 'bash' || m === 'sh' || m === 'shell') return 'shell';
    if (m === 'js' || m === 'javascript') return 'javascript';
    if (m === 'ts' || m === 'typescript') return 'typescript';
    if (m === 'html' || m === 'html5') return 'html';
    if (m === 'css' || m === 'css3') return 'css';
    if (m === 'py' || m === 'python') return 'python';
    if (m === 'text' || m === 'plaintext') return 'plaintext';
    return m;
  };

  // No mapping: cfg.options must be valid Monaco editor options

  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      // Hier können Sie benutzerdefinierte Autovervollständigungen oder andere spezielle Anpassungen für den Monaco-Editor hinzufügen.
    }
  }, []);

  const handleEditorChange = (newValue, e) => {
    props.codeChanged(newValue);
    try {
      if (baselineRef.current === undefined) baselineRef.current = props.code ?? '';
      const modified = String(newValue) !== String(baselineRef.current || '');
      if (props.onModifiedChange) props.onModifiedChange(modified);
    } catch {}
  };

  // Merge options once per render
  const combinedOptions = { ...(props.cfg?.options || {}), ...(props.projectOptions || {}) };
  // Ensure reasonable defaults without overriding provided options
  if (combinedOptions.automaticLayout === undefined) combinedOptions.automaticLayout = true;
  if (combinedOptions.scrollBeyondLastLine === undefined) combinedOptions.scrollBeyondLastLine = false;
  if (combinedOptions.contextmenu === undefined) combinedOptions.contextmenu = true;

  const isDark = props.currentTheme === 'dark';
  // Use dark/light theme based on isDark, unless combinedOptions.theme is explicitly set to something other than the default
  const defaultTheme = isDark ? 'learningThemeDark' : 'learningTheme';
  const desiredTheme = (combinedOptions && combinedOptions.theme && combinedOptions.theme !== 'learningTheme' && combinedOptions.theme !== 'learningThemeDark') 
    ? combinedOptions.theme 
    : defaultTheme;
  
  // Remove theme from combinedOptions so it doesn't override the theme prop
  const { theme: _, ...optionsWithoutTheme } = combinedOptions;
  
  console.log('[CodeEditorMonaco] Render - props.currentTheme:', props.currentTheme, 'isDark:', isDark, 'combinedOptions.theme:', combinedOptions?.theme, 'defaultTheme:', defaultTheme, 'desiredTheme:', desiredTheme);

  const handleBeforeMount = (monaco: any) => {
    monacoRef.current = monaco;
    try {
      // Preload language contributions via AMD loader so language isn't plaintext at first paint
      const amdRequire = (window as any).require;
      if (amdRequire && typeof amdRequire === 'function') {
        try {
          amdRequire([
            'vs/language/typescript/monaco.contribution',
            'vs/language/html/monaco.contribution',
            'vs/language/css/monaco.contribution',
            'vs/language/json/monaco.contribution'
          ], () => {});
        } catch {}
      }
      // Light theme
      monaco.editor.defineTheme('learningTheme', {
        base: 'vs',
        inherit: true,
        rules: [
          { token: 'comment', foreground: '6a9955', fontStyle: 'italic' },
          { token: 'keyword', foreground: '0000ff', fontStyle: 'bold' },
          { token: 'string', foreground: 'a31515' },
          { token: 'number', foreground: '098658' },
          { token: 'function', foreground: '795e26' }
        ],
        colors: {
          'editor.foreground': '#2d3748',
          'editor.background': '#ffffff',
          'editor.selectionBackground': '#007acc40',
          'editor.lineHighlightBackground': '#f8f9fa',
          'editorLineNumber.foreground': '#a0aec0',
          'editorCursor.foreground': '#007acc'
        }
      });
      // Dark theme
      monaco.editor.defineTheme('learningThemeDark', {
        base: 'vs-dark',
        inherit: true,
        rules: [
          { token: 'comment', foreground: '6a9955', fontStyle: 'italic' },
          { token: 'keyword', foreground: '569cd6', fontStyle: 'bold' },
          { token: 'string', foreground: 'ce9178' },
          { token: 'number', foreground: 'b5cea8' },
          { token: 'function', foreground: 'dcdcaa' }
        ],
        colors: {
          'editor.foreground': '#e5e7eb',
          'editor.background': '#1e1e1e',
          'editor.selectionBackground': '#264f78',
          'editor.lineHighlightBackground': '#2a2d2e',
          'editorLineNumber.foreground': '#858585',
          'editorCursor.foreground': '#aeafad'
        }
      });
    } catch {}
  };

  const handleEditorMount = (editor, monaco) => {
    editorRef.current = editor;
    try { (window as any).__monaco_editor__ = editor; } catch {}
    try {
      console.groupCollapsed('[CodeEditorMonaco] editorDidMount');
      console.log('Monaco available:', !!monaco);
      console.log('Language prop:', mode);
      console.log('Desired theme:', desiredTheme);
      console.log('Options (combined):', optionsWithoutTheme);
      // Ensure theme is applied after definition (important on initial mount)
      monaco.editor.setTheme(desiredTheme);
      // Ensure contextmenu is on (even if overridden elsewhere)
      editor.updateOptions({ contextmenu: optionsWithoutTheme.contextmenu !== false });
      // Log current model language and correct if needed (single guard)
      try {
        const model = editor.getModel && editor.getModel();
        const lang = model?.getLanguageId ? model.getLanguageId() : (model?.getModeId ? model.getModeId() : undefined);
        console.log('Editor model language:', lang);
        if (model && mode && lang !== mode) {
          monaco.editor.setModelLanguage(model, mode);
          console.log('[CodeEditorMonaco] corrected model language ->', mode);
        }
        // Baseline equals initial code
        try { baselineRef.current = props.code ?? ''; } catch {}
        // Initial status emit
        if (props.onStatusChange) {
          const pos = editor.getPosition();
          props.onStatusChange({ lines: model?.getLineCount?.() || 0, lineNumber: pos?.lineNumber || 1, column: pos?.column || 1 });
        }
        // Listen for status changes
        try {
          editor.onDidChangeCursorPosition(() => {
            if (!props.onStatusChange) return;
            const m = editor.getModel();
            const p = editor.getPosition();
            props.onStatusChange({ lines: m?.getLineCount?.() || 0, lineNumber: p?.lineNumber || 1, column: p?.column || 1 });
          });
        } catch {}
        try {
          model?.onDidChangeContent?.(() => {
            if (!props.onStatusChange) return;
            const p = editor.getPosition();
            props.onStatusChange({ lines: model?.getLineCount?.() || 0, lineNumber: p?.lineNumber || 1, column: p?.column || 1 });
          });
        } catch {}
      } catch (e) {}
      // Listen context menu open
      try {
        editor.onContextMenu((e) => {
          console.log('[CodeEditorMonaco] onContextMenu', { target: e?.target });
        });
      } catch (e) {}
      // expose controls
      if (props.controlsRef) {
        props.controlsRef.current = {
          undo: () => { try { editor.trigger('keyboard', 'undo', null); } catch {} },
          redo: () => { try { editor.trigger('keyboard', 'redo', null); } catch {} },
        };
      }
      console.groupEnd();
    } catch (e) {}
  };

  // Update Monaco theme when currentTheme prop changes
  useEffect(() => {
    const monaco = monacoRef.current;
    if (monaco) {
      try {
        const isDarkNow = props.currentTheme === 'dark';
        const newTheme = isDarkNow ? 'learningThemeDark' : 'learningTheme';
        console.log('[CodeEditorMonaco] useEffect - Updating theme to:', newTheme, 'props.currentTheme:', props.currentTheme, 'isDarkNow:', isDarkNow);
        monaco.editor.setTheme(newTheme);
      } catch (e) {
        console.error('[CodeEditorMonaco] Failed to update Monaco theme:', e);
      }
    }
  }, [props.currentTheme]);

  let mode = getMode(props.mode);
  if (!mode) {
    mode = "plaintext";
  }

  // No custom uri/model path required – rely on language prop and contributions

  return (
    <MonacoEditorAny
      key={`monaco-${mode}`}
      width={props.width || "100%"}
      height={props.height || "100%"}
      language={mode}
      theme={desiredTheme}
      value={props.code}
      options={optionsWithoutTheme}
      onChange={handleEditorChange}
      editorWillMount={handleBeforeMount}
      editorDidMount={handleEditorMount}
    />
  );
}

export default CodeEditorMonaco;
